import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SparkleEffect } from '../../components/SparkleEffect';
import { GlitterHeart } from '../../components/GlitterHeart';
import { CalendarIcon, RepeatIcon, RefreshCwIcon, SparklesIcon, SmileIcon, MessageCircleHeartIcon, ZapIcon, CheckSquareIcon, DumbbellIcon, HomeIcon, PaletteIcon, ShieldCheckIcon, ArrowRightIcon } from 'lucide-react';
export function TrippinThroughLifePage() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const formData = new FormData();
      formData.append('fields[email]', email);
      formData.append('ml-submit', '1');
      formData.append('anticsrf', 'true');
      await fetch('https://assets.mailerlite.com/jsonp/2211088/forms/182587044822779312/subscribe', {
        method: 'POST',
        body: formData,
        mode: 'no-cors'
      });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };
  const features = [{
    icon: <CalendarIcon className="w-6 h-6" data-id="element-819" />,
    title: 'AI-powered personalized schedules'
  }, {
    icon: <RepeatIcon className="w-6 h-6" data-id="element-820" />,
    title: 'Adaptive routines and recurring chore systems'
  }, {
    icon: <RefreshCwIcon className="w-6 h-6" data-id="element-821" />,
    title: 'Flexible and rotating schedule support'
  }, {
    icon: <SparklesIcon className="w-6 h-6" data-id="element-822" />,
    title: 'Deep cleaning checklists and home reset plans'
  }, {
    icon: <SmileIcon className="w-6 h-6" data-id="element-823" />,
    title: 'Mood check-ins and wellness support'
  }, {
    icon: <MessageCircleHeartIcon className="w-6 h-6" data-id="element-824" />,
    title: 'Daily affirmations and motivation'
  }, {
    icon: <ZapIcon className="w-6 h-6" data-id="element-825" />,
    title: 'Personalized "Tiny Badass Actions"'
  }, {
    icon: <CheckSquareIcon className="w-6 h-6" data-id="element-826" />,
    title: 'AI-generated custom checklists'
  }, {
    icon: <DumbbellIcon className="w-6 h-6" data-id="element-827" />,
    title: 'Fitness and self-care routine tracking'
  }, {
    icon: <HomeIcon className="w-6 h-6" data-id="element-828" />,
    title: 'Family and household task organization'
  }, {
    icon: <PaletteIcon className="w-6 h-6" data-id="element-829" />,
    title: 'Customizable themes and personalized app experience'
  }];
  return <div className="min-h-screen bg-gradient-to-b from-blush-50 via-white to-peach-50 pt-28 pb-24" data-id="element-830">
      <SparkleEffect count={80} data-id="element-831" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-832">
        {/* Hero Section */}
        <div className="text-center mb-16" data-id="element-833">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="w-32 h-32 md:w-48 md:h-48 mx-auto mb-8 rounded-3xl bg-white shadow-xl shadow-blush-200/50 flex items-center justify-center p-4 border border-blush-100" data-id="element-834">
            <img src="https://cdn.magicpatterns.com/uploads/gieTJfs2YUH6aZyKV1oyEk/ChatGPT_Image_May_11,_2026,_01_53_32_PM.png" alt="Trippin' Through Life App Logo" className="w-full h-full object-contain" data-id="element-835" />
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="font-heading text-4xl md:text-6xl font-bold text-plum-800 mb-6" data-id="element-836">
            Trippin' Through Life
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-xl text-plum-600 font-medium max-w-3xl mx-auto leading-relaxed" data-id="element-837">
            Your AI-powered life, home, and wellness management system designed
            to reduce overwhelm, build routines, and help you stay on top of
            everyday life — without burnout.
          </motion.p>
        </div>

        {/* Description */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-blush-100 mb-16" data-id="element-838">
          <p className="text-plum-700 text-lg leading-relaxed mb-6" data-id="element-839">
            Using intelligent conversational onboarding, TTL learns your unique
            lifestyle, household structure, schedules, routines, stress levels,
            cleaning needs, and personal goals to create fully personalized
            daily plans, chore systems, reset routines, wellness check-ins, and
            smart checklists tailored specifically to you.
          </p>
          <p className="text-plum-700 text-lg leading-relaxed font-medium" data-id="element-840">
            Whether you're managing a busy family, flexible work schedule,
            content creation, school routines, mental overload, fitness goals,
            or simply trying to get your life back on track, TTL adapts to your
            real life — not the other way around.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="mb-20" data-id="element-841">
          <h2 className="font-heading text-3xl font-bold text-center text-plum-800 mb-10" data-id="element-842">
            Everything you need to thrive
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-843">
            {features.map((feature, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.05
          }} className="flex items-center gap-4 bg-white/60 backdrop-blur-sm p-5 rounded-2xl border border-blush-200/50 shadow-sm hover:bg-white transition-colors" data-id="element-844">
                <div className="w-12 h-12 rounded-xl bg-blush-100 flex items-center justify-center text-cta-500 shrink-0" data-id="element-845">
                  {feature.icon}
                </div>
                <span className="font-bold text-plum-700" data-id="element-846">{feature.title}</span>
              </motion.div>)}
          </div>
        </div>

        {/* Tagline & Waitlist */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center bg-gradient-to-br from-plum-900 to-plum-800 rounded-[3rem] p-10 md:p-16 shadow-2xl relative overflow-hidden mb-20" data-id="element-847">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cta-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-848" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blush-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" data-id="element-849" />

          <div className="relative z-10" data-id="element-850">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-851">
              From survival mode to thriving mode.
            </h2>
            <p className="text-plum-200 text-lg mb-10 max-w-xl mx-auto" data-id="element-852">
              Trippin' Through Life helps turn chaos into structure, one
              manageable step at a time.
            </p>

            <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20 max-w-md mx-auto" data-id="element-853">
              {status === 'success' ? <div className="text-center py-4" data-id="element-854">
                  <GlitterHeart size={40} variant="light" className="mx-auto mb-4" data-id="element-855" />
                  <p className="font-heading text-2xl font-bold text-white mb-2" data-id="element-856">
                    You're on the list!
                  </p>
                  <p className="text-plum-200 font-medium" data-id="element-857">
                    We'll let you know as soon as the app is ready.
                  </p>
                </div> : <>
                  <h3 className="font-bold text-white mb-4 text-xl" data-id="element-858">
                    Get early access
                  </h3>
                  <form className="flex flex-col gap-3" onSubmit={handleSubmit} data-id="element-859">
                    <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" className="w-full px-5 py-4 rounded-xl border border-white/30 bg-white/20 text-white placeholder:text-plum-200 focus:ring-2 focus:ring-cta-400 outline-none backdrop-blur-sm" data-id="element-860" />
                    <button type="submit" disabled={status === 'loading'} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-cta-500 text-white rounded-xl font-bold hover:bg-cta-400 transition-colors disabled:opacity-60 shadow-lg shadow-cta-500/30" data-id="element-861">
                      <GlitterHeart size={18} variant="light" data-id="element-862" />
                      {status === 'loading' ? '...' : 'Join Waitlist'}
                    </button>
                  </form>
                  {status === 'error' && <p className="text-red-300 text-sm font-medium mt-3" data-id="element-863">
                      Something went wrong. Please try again.
                    </p>}
                  <p className="text-xs text-plum-200/80 mt-4 leading-relaxed text-center" data-id="element-864">
                    By subscribing, you agree to our{' '}
                    <Link to="/terms" className="underline hover:text-white transition-colors" data-id="element-865">
                      Terms & Conditions
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="underline hover:text-white transition-colors" data-id="element-866">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </>}
            </div>
          </div>
        </motion.div>

        {/* Privacy Policy Link Card */}
        <Link to="/apps/trippin-through-life/privacy" className="group block bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-blush-200 hover:shadow-lg hover:border-blush-300 transition-all" data-id="element-867">
          <div className="flex items-center gap-5" data-id="element-868">
            <div className="w-14 h-14 rounded-2xl bg-blush-100 flex items-center justify-center shrink-0 group-hover:bg-blush-200 transition-colors" data-id="element-869">
              <ShieldCheckIcon className="w-7 h-7 text-cta-500" data-id="element-870" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-871">
              <h3 className="font-heading text-xl font-bold text-plum-800 mb-1" data-id="element-872">
                Privacy Policy
              </h3>
              <p className="text-plum-500 text-sm font-medium" data-id="element-873">
                How we handle your data — effective May 12, 2026
              </p>
            </div>
            <ArrowRightIcon className="w-5 h-5 text-plum-400 group-hover:text-cta-500 group-hover:translate-x-1 transition-all shrink-0" data-id="element-874" />
          </div>
        </Link>
      </div>
    </div>;
}